<script setup>
import requests from "@dk.onlinefundraising/onboarding-utils/src/OfOnboardingRequests";
import { ref, defineEmits } from "vue";
import OfModalBlock from "@dk.onlinefundraising/vue-utils/src/utils/OfModalBlock.vue";
import OfButton from "@dk.onlinefundraising/vue-utils/src/utils/OfButton.vue";
import { toaster } from "@dk.onlinefundraising/vue-utils/src/helpers/OfToaster.js";

const urlParams = new URLSearchParams(window.location.search);
const action = ref(urlParams.get("action") ?? "Login");
const email = ref(null);
const password = ref(null);
const password2 = ref(null);
const token = ref(urlParams.get("token"));

const emit = defineEmits(["loggedIn"]);

function sendLogin() {
  //  Validation
  if (!email.value) {
    toaster.error("Missing email");
    return;
  } else if (!password.value) {
    toaster.error("Missing password");
    return;
  }

  const creds = {
    email: email.value,
    password: password.value,
  };

  requests.login(creds).then((result) => {
    if (result) {
      localStorage.setItem("authToken", result.authToken.authToken);

      const nowInSeconds = Math.round(new Date().getTime() / 1000);
      const oneDay = 24 * 3600;
      localStorage.setItem("authExp", nowInSeconds + oneDay);

      password.value = null;

      emit("loggedIn");
    }
  });
}
function sendChangePassword() {
  //  Validation
  if (!password.value) {
    toaster.error("Missing password");
    return;
  } else if (!password2.value) {
    toaster.error("Missing password");
    return;
  } else if (password.value !== password2.value) {
    toaster.error("Passwords are not the same");
    return;
  } else if (password.value.length < 12) {
    toaster.error("The password has to be at least 12 characters long.");
    return;
  }
  

  requests.changePassword(password.value, token.value).then(() => {
    toaster.success("Password changed");
    password2.value = null;
    urlParams.delete("action");
    urlParams.delete("token");
    action.value = "Login";
  });
}
function sendForgotPassword() {
  //  Validation
  if (!email.value) {
    toaster.error("Missing email");
    return;
  }

  requests.forgotPassword(email.value).then(() => {});
}
function checkEnter(event) {
  if (event.key === "Enter" || event.keyCode === 13) {
    if (action.value === "Login") {
      sendLogin();
    } else if (action.value === "ForgotPassword") {
      sendForgotPassword();
    } else if (action.value === "ChangePassword") {
      sendChangePassword();
    }
  }
}
</script>
<template>
  <OfModalBlock>
    <template #block-content>
      <div class="login">
        <div class="login-inner">
          <div v-if="action === 'ChangePassword'">
            <h1>Change password</h1>
            <p>Please insert your new password twice</p>
          </div>

          <div v-else-if="action === 'ForgotPassword'">
            <h1>Lost your password?</h1>
            <p>
              Please enter your username or email address. You will receive an
              email message with instructions on how to reset your password.
            </p>
          </div>

          <div v-else>
            <h1>Login</h1>
            <p>Please insert your email address and password</p>
          </div>

          <form
            method="post"
            action=""
            autocomplete="nope"
            novalidate="novalidate"
            @submit.prevent
            @keydown="checkEnter"
          >
            <!-- Email -->
            <div
              v-if="action === 'Login' || action === 'ForgotPassword'"
              class="control-input"
            >
              <input
                type="email"
                id="email"
                placeholder="Email address"
                v-model="email"
                autocomplete="off"
                required
              />
            </div>

            <!-- Password -->
            <div
              v-if="action === 'Login' || action === 'ChangePassword'"
              class="control-input"
            >
              <input
                type="password"
                id="password"
                placeholder="Password"
                v-model="password"
                required
              />
            </div>

            <!-- Password -->
            <div v-if="action === 'ChangePassword'" class="control-input">
              <input
                type="password"
                id="password2"
                placeholder="Password again"
                v-model="password2"
                required
              />
            </div>

            <!-- Buttons -->
            <div class="form-footer">
              <OfButton
                v-if="action === 'ChangePassword'"
                text="Change password"
                coloring="primary"
                @click="sendChangePassword()"
              />

              <OfButton
                v-else-if="action === 'ForgotPassword'"
                text="Get new password"
                coloring="primary"
                @click="sendForgotPassword()"
              />

              <OfButton
                v-else-if="action === 'Login'"
                text="Login"
                coloring="primary"
                @click="sendLogin()"
              />
            </div>
            <div class="links">
              <a
                v-if="action === 'ForgotPassword'"
                type="button"
                @click.prevent="action = 'Login'"
              >
                Go to login
              </a>
              <a
                v-else-if="action === 'Login'"
                @click.prevent="action = 'ForgotPassword'"
              >
                Lost your password?
              </a>
            </div>
          </form>
        </div>
      </div>
    </template>
  </OfModalBlock>
</template>
<style scoped>
.login {
  -webkit-font-smoothing: antialiased;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 0;
  font: inherit;
  vertical-align: baseline;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  height: 100vh;
  -webkit-box-pack: center;
  justify-content: center;
  background: linear-gradient(-16deg, #0f5bc2, #3ab1e6);
}

.login-inner {
  margin: 0 auto;
  max-width: 450px;
  width: 100%;
  background: #fff;
  padding: 0 30px 30px;
  border-radius: 4px;
  box-shadow: 0 1px 20px 0 rgba(0, 0, 0, 0.15);
  border: 1px solid #eaedf3;
  text-align: center;
}

.login-inner > div {
  text-align: center;
  padding: 40px 30px 25px;
  margin-bottom: 15px;
}

.login-inner > div > h1 {
  font-weight: 500;
  margin-bottom: 15px;
  line-height: 1.2;
  margin: 0 0 10px;
  font-size: 1.4rem;
  font-family: Roboto, sans-serif;
}

.login-inner > div > p {
  color: rgba(0, 0, 0, 0.54);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  font-family: Roboto, sans-serif;
}
.form-footer {
  border-top: 1px solid #eaedf3;
  margin-top: 30px;
  padding: 30px 0 0;
}
.links {
  margin-top: 16px;
}
.links a {
  color: #0f5bc2;
  position: relative;
}
</style>
